import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

function NavItem({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
          isActive ? 'text-white' : 'text-white/55 hover:text-white/90'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

/** App shell: fixed cinematic nav + scrollable content. */
export default function Layout() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Every navigation should start at the top, like a real streaming app.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="min-h-full bg-ink-950">
      <header
        className={`fixed inset-x-0 top-0 z-40 transition-all duration-300 ${
          scrolled
            ? 'border-b border-white/5 bg-ink-950/85 backdrop-blur-xl'
            : 'bg-gradient-to-b from-black/80 to-transparent'
        }`}
      >
        <nav className="mx-auto flex h-16 max-w-[1600px] items-center gap-3 px-4 sm:gap-6 sm:px-6 lg:px-10">
          <NavLink to="/" className="focus-ring flex shrink-0 items-center gap-2.5 rounded">
            <span className="text-accent text-2xl leading-none font-extrabold tracking-tight">M</span>
            <span className="hidden text-lg font-bold tracking-tight sm:block">
              Memory<span className="text-white/55">flix</span>
            </span>
          </NavLink>

          <div className="flex items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <NavItem to="/">Home</NavItem>
            <NavItem to="/library">Library</NavItem>
            {/* On phones the "Add memories" button already leads here. */}
            <span className="hidden sm:contents">
              <NavItem to="/import">Import</NavItem>
            </span>
          </div>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <NavLink
              to="/create"
              className="focus-ring rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold whitespace-nowrap text-black sm:px-3.5 transition hover:bg-white/85"
              aria-label="Make an episode"
            >
              <span className="sm:hidden">+ Episode</span>
              <span className="hidden sm:inline">Make an episode</span>
            </NavLink>
            <NavLink
              to="/import"
              className="focus-ring rounded-md bg-accent px-2.5 py-1.5 text-sm font-semibold whitespace-nowrap text-white sm:px-3.5 transition hover:bg-accent-soft"
              aria-label="Add memories"
            >
              <span className="sm:hidden">Import</span>
              <span className="hidden sm:inline">Add memories</span>
            </NavLink>
            <AccountMenu />
          </div>
        </nav>
      </header>

      <main className="pt-16">
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-white/5 px-4 py-10 text-center text-xs text-white/25 sm:px-6">
        Memoryflix — your life, beautifully organized.
      </footer>
    </div>
  );
}
/** Initial in a circle; opens Sharing and Sign out. */
function AccountMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;
  const initial = (user.name.trim()[0] ?? '?').toUpperCase();

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Account: ${user.name}`}
        className="focus-ring grid h-8 w-8 place-items-center rounded-full bg-white/15 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/25"
      >
        {initial}
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-56 overflow-hidden rounded-xl bg-ink-900 py-1 shadow-2xl ring-1 ring-white/10">
          <div className="border-b border-white/[0.06] px-4 py-3">
            <p className="truncate text-sm font-semibold">{user.name}</p>
            {user.email && <p className="truncate text-xs text-white/45">{user.email}</p>}
          </div>
          <NavLink role="menuitem" to="/sharing" className="block px-4 py-2.5 text-sm text-white/80 hover:bg-white/[0.06] hover:text-white">
            Sharing
          </NavLink>
          <button
            role="menuitem"
            type="button"
            onClick={() => void signOut()}
            className="block w-full px-4 py-2.5 text-left text-sm text-white/80 hover:bg-white/[0.06] hover:text-white"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
